/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import apiClient from '../../../services/api-client';
import { RootState } from '../../../store/rootReducer';
import AuthSlice from '../../azure-b2c/storage';
import {
  GetProductsReducerAction,
  Product,
  ProductsApiResponse,
  ProductsState,
  StockAvailabilities,
  StockAvailabilitiesPayload,
} from './types';
import { storageDb } from '../../../services/localforage';
import Transforms from '../../../store/setTransform';
import { PersistConfig } from 'redux-persist';

export const getStockAvailabilities = async ({ productIds }: StockAvailabilitiesPayload) => {
  try {
    const response = await apiClient.post<StockAvailabilities, StockAvailabilitiesPayload>(
      `/products/get-availabilities`,
      { productIds },
    );

    if (response.status.toString().startsWith('2')) {
      return response.data.availabilities;
    }
    return undefined;
  } catch (error) {
    console.log(error);
  }
};

export type ActivePrice = {
  productId: number;
  basePriceExcGst: number;
  discountedPriceExcGst: number;
  discountedPriceIncGst: number;
};
export type ActivePrices = {
  prices: ActivePrice[];
};
export const getActivePrices = async (
  accountNumber: string | undefined,
  productIds: number[],
  branchId: string | undefined,
) => {
  try {
    const response = await apiClient.post<
      ActivePrices,
      {
        accountNumber: unknown;
        productIds: number[];
        branchId: string | undefined;
      }
    >(`/products/get-active-prices`, {
      accountNumber: accountNumber,
      productIds: productIds,
      branchId: branchId,
    });

    return response;
  } catch (error) {
    console.log(error);
  }
};

export const getActivePricesForPastProducts = async (
  accountNumber: string | undefined,
  products: Product[],
  branchId: string | undefined,
) => {
  try {
    const productIds = products.map((p) => p.productId);
    const productsHaveAlt = products.filter((p) => p.alternativeProduct);
    const altProductIds = productsHaveAlt
      .map((p) => p.alternativeProduct?.productId)
      .filter((id) => id !== undefined);

    // Run both promises simultaneously
    const [activePricesResponse, activePricesAltResponse] = await Promise.all([
      getActivePrices(accountNumber, productIds, branchId),
      getActivePrices(accountNumber, altProductIds, branchId),
    ]);

    // Handle the responses
    const prices = activePricesResponse?.data?.prices.filter((ap) => ap !== undefined);
    if (!prices || prices.length === 0) {
      return;
    }

    const altPrices = activePricesAltResponse?.data?.prices.filter(
      (price) => price.basePriceExcGst > 0,
    );
    if (!altPrices) {
      return;
    }
    // Update alternative products with activePrices in Products
    const productsWithUpdatedAltProducts = updateAltPrices(
      products,
      productsHaveAlt,
      altProductIds,
      altPrices,
    ).filter((product) => product !== undefined);

    // Update normal products with active prices
    return productsWithUpdatedAltProducts.map((product) => {
      const activePrice = prices.find((ap) => ap.productId === product.productId);
      return activePrice ? { ...product, ...activePrice } : product;
    });
  } catch (error) {
    console.log(error);
  }
};

const transformProducts = (data: ProductsApiResponse): Product[] => {
  const products = data.products.filter((a) => a.orders.length > 0);

  return products.map(
    (x) =>
      ({
        ...x,
        category: x.category ?? 'Miscellaneous',
        discountedPriceExcGst: x.discountedPriceExcGst,
        discountedPriceIncGst: x.discountedPriceIncGst,
        basePriceIncGst: x.nationalPrice,
        basePriceExcGst: x.basePriceExcGst,
      }) as Product,
  );
};

const updateAltPrices = (
  products: Product[],
  productsHaveAlt: Product[],
  altProductIds: number[],
  prices: ActivePrice[],
) => {
  // Map active prices for alternative products
  const altProductPrices = prices.filter((price) => altProductIds.includes(price.productId));

  // Update products with alternative product prices
  const altProductsWithActivePrices = productsHaveAlt
    .map((product) => {
      const altProductPrice = altProductPrices.find(
        (ap) => ap.productId === product.alternativeProduct?.productId,
      );
      if (altProductPrice) {
        return {
          ...product,
          alternativeProduct: {
            ...product.alternativeProduct,
            ...altProductPrice,
            basePriceExcGst: 111, // test value
          },
        };
      }
    })
    .filter((alt) => alt !== undefined && alt.basePriceExcGst > 0);

  if (altProductsWithActivePrices.length > 0) {
    // Update the main products array with active prices
    const productsWithUpdatedAltProducts = products.map((product) => {
      if (product.alternativeProduct) {
        const targetProduct = altProductsWithActivePrices.find(
          (x) => x?.productId === product.productId,
        );
        return targetProduct;
      }
      return product;
    }) as Product[];

    return productsWithUpdatedAltProducts;
  }

  return products;
};

/**
 * Fetches a list of all past products for the specified account number and branch ID.
 * Sends a GET request to the `/past-products` endpoint with the provided query parameters.
 * Transforms the fetched products using the `transformProducts` function and returns the transformed products along with the account number.
 *
 * @function getPastProducts
 * @param {Object} params - The parameters for the API request.
 * @param {string} params.accountNumber - The account number for which past products need to be fetched.
 * @param {string | undefined} params.branchId - The branch ID for which the product prices are to be returned. If the branch ID is not provided, the API will fetch data without filtering by branch.
 * @returns {Promise<GetProductsReducerAction>} An object containing the transformed products and the account number.
 * @typedef {Object} GetProductsReducerAction
 * @property {Array} products - A list of transformed past products for the given account number.
 * @property {string} accountNumber - The account number provided in the request.
 */
export const getPastProducts = createAsyncThunk<
  GetProductsReducerAction,
  { accountNumber: string }
>('products', async ({ accountNumber }) => {
  const request = await apiClient.get<ProductsApiResponse, AxiosError>(
    encodeURI(`v2/past-products?accountNumber=${accountNumber}`),
  );

  return { products: transformProducts(request.data), accountNumber: accountNumber };
});

export const initialState: ProductsState = {
  products: {},
  status: {},
};

export const productsPersistConfig: PersistConfig<ProductsState> = {
  key: 'products',
  storage: storageDb,
  // serialize: false,
  transforms: Transforms,
  blacklist: ['status'],
};

const PastProductsSlice = createSlice({
  name: 'products',
  initialState: initialState,
  extraReducers: (builder) => {
    builder.addCase(AuthSlice.actions.setSelectedFarmerAccount, (state, { payload }) => {
      state.status[payload] = 'idle';
    });
    builder.addCase(getPastProducts.fulfilled, (state, action) => {
      state.products[action.payload.accountNumber] = action.payload.products;
      state.status[action.payload.accountNumber] = 'fulfilled';
    });
    builder.addCase(getPastProducts.pending, (state, { meta }) => {
      state.status[meta.arg.accountNumber] = 'pending';
    });
    builder.addCase(getPastProducts.rejected, (state, { meta }) => {
      state.status[meta.arg.accountNumber] = 'fulfilled';
    });
  },
  reducers: {
    initPastProducts(
      state: ProductsState,
      { payload: { accountNumber } }: PayloadAction<{ accountNumber: string }>,
    ) {
      state.products[accountNumber] = [];
    },
  },
});

const selectState = (state: RootState) => state;

export const selectPastProducts = createSelector(selectState, (state) => {
  if (state.auth.selectedAccountId) {
    const accountId = state.auth.selectedAccountId;
    const propertyId = state.auth.accounts[accountId].digitalFarmer?.selectedAccountNumber;

    if (propertyId && Object.keys(state.products.products).indexOf(propertyId) !== -1) {
      return state.products.products[propertyId] ?? [];
    }
  }

  return [];
});

export const selectCurrentStatus = createSelector(selectState, (state) => {
  if (state.auth.selectedAccountId) {
    const accountId = state.auth.selectedAccountId;
    const propertyId = state.auth.accounts[accountId].digitalFarmer?.selectedAccountNumber;

    if (propertyId && Object.keys(state.products.status).indexOf(propertyId) !== -1) {
      return state.products.status[propertyId];
    }
  }

  return 'idle';
});

export const selectItemNumbers = createSelector(selectState, (state) => {
  if (state.auth.selectedAccountId) {
    const accountId = state.auth.selectedAccountId;
    const propertyId = state.auth.accounts[accountId].digitalFarmer?.selectedAccountNumber;

    if (propertyId && Object.keys(state.products.products).indexOf(propertyId) !== -1) {
      const products = state.products.products[propertyId];
      const itemNumbers = new Set(products?.map((x) => x.itemNumber));
      return itemNumbers;
    }
  }

  return new Set();
});

export default PastProductsSlice;
